import React, { useState, useRef, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import {
  MdSearch,
  MdNotifications,
  MdEmail,
  MdPerson,
  MdSettings,
  MdSwapHoriz,
  MdClose,
  MdMenu,
} from 'react-icons/md';
import { FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import Swal from 'sweetalert2';
import axios from 'axios';
import MyContext from '../../../context/MyContext';
import '../../../assets/css/components/seller/topbar/SellerTopbar.css';
import { getEcho } from '../../general/echo/Echo';

const getTimeAgo = (dateString) => {
  const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};

// Notification "unread" ki definition robust ban chuki hai — status ki value
// database mein 'unread', 'Unread', ya kabhi kabhi garbage (0, null) bhi ho
// sakti hai. Is liye "read" ke elawa sab kuch unread mana jata hai.
const isUnread = (status) => String(status).toLowerCase() !== 'read';

const SellerTopbar = ({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen, isMobile }) => {
  const navigate = useNavigate();
  const { baseUrl, loggedInSeller, authToken, setAuthToken, setIsLoggedIn, setLoggedInCustomer } = useContext(MyContext);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [conversations, setConversations] = useState([]);
  const notificationRef = useRef(null);
  const messageRef = useRef(null);

  // ── FIXED: topbar pehle sirf context/localStorage wale
  // "loggedInSeller" pe depend karta tha, jo stale ho sakta hai
  // (purana/kisi aur seller ka data reh jana). Ab dashboard ki tarah
  // /seller/profile/viewProfile se fresh seller fetch karte hain,
  // taake topbar hamesha DB ka asal, up-to-date naam dikhaye ──
  const [freshSeller, setFreshSeller] = useState(null);

  const fetchSellerProfile = () => {
    if (!authToken) return;

    axios
      .post(
        `${baseUrl}/seller/profile/viewProfile`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      .then((response) => {
        if (response.data.success) {
          setFreshSeller(response.data.seller);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    fetchSellerProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // fresh API data available ho to wahi use karo, warna context wale
  // par fallback rakho (taake pehle render pe topbar khali na dikhe)
  const displaySeller = freshSeller || loggedInSeller;

  const fetchNotifications = () => {
    axios.get(`${baseUrl}/seller/notifications/viewAllNotifications`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((response) => {
        if (response.data.success) {
          setNotifications(response.data.notifications);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const fetchConversations = () => {
    axios.get(`${baseUrl}/seller/conversations/viewAllConversations`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((response) => {
        if (response.data.success) {
          setConversations(response.data.conversations);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (!authToken) return;
    fetchNotifications();
    fetchConversations();
  }, [authToken]);

  // ── real-time — nayi notification aate hi list ke top pe daal do,
  // badge count apne aap update ho jayega kyunke unreadNotifications
  // usi notifications state se calculate hota hai ──
  useEffect(() => {
    if (!authToken || !loggedInSeller?.id) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channelName = `seller.${loggedInSeller.id}`;
    const channel = echo.private(channelName);

    channel.listen('.new-notification', (e) => {
      const incoming = e.notification;
      setNotifications((prev) => {
        if (prev.some((n) => n.id === incoming.id)) return prev;
        return [incoming, ...prev];
      });
    });

    return () => {
      echo.leave(channelName);
    };
  }, [authToken, loggedInSeller?.id]);

  useEffect(() => {
    const clickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) setShowNotifications(false);
      if (messageRef.current && !messageRef.current.contains(e.target)) setShowMessages(false);
    };
    document.addEventListener('mousedown', clickOutside);
    return () => document.removeEventListener('mousedown', clickOutside);
  }, []);

  // ── notification pe click: mark as read + notifications page
  // par navigate karo us particular notification ke sath, taake
  // wahan uski details modal khul jaye ──
  const openNotification = (n) => {
    setShowNotifications(false);

    if (isUnread(n.status)) {
      const formData = new FormData();
      formData.append('id', n.id);

      axios
        .post(`${baseUrl}/seller/notifications/changeNotificationStatus`, formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then(() => {
          setNotifications((prev) =>
            prev.map((item) =>
              item.id === n.id ? { ...item, status: 'read' } : item,
            ),
          );
        })
        .catch((error) => console.log(error));
    }

    navigate('/seller/notifications', { state: { openNotification: n } });
  };

  const switchToUser = () => {
    Swal.fire({
      title: 'Switch to User Account?',
      text: 'You will be redirected to the user dashboard',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Switch',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#097d43',
    }).then((result) => {
      if (!result.isConfirmed) return;

      axios.post(`${baseUrl}/seller/auth/switchToUser`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      })
        .then((response) => {
          if (response.data.success) {
            localStorage.setItem('authToken', response.data.token);
            localStorage.setItem('loggedInCustomer', JSON.stringify(response.data.customer));
            setAuthToken(response.data.token);
            setLoggedInCustomer(response.data.customer);
            setIsLoggedIn(true);
            Swal.fire({
              title: 'Switched!',
              text: 'Successfully switched to user account',
              icon: 'success',
              confirmButtonColor: '#097d43',
              timer: 1500
            }).then(() => navigate('/customer/dashboard'));
          } else {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not switch account.' });
          }
        })
        .catch((error) => {
          console.log(error);
          Swal.fire({ icon: 'error', title: 'Error', text: 'Could not switch account.' });
        });
    });
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setIsMobileOpen(!isMobileOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const unreadNotifications = notifications.filter(n => isUnread(n.status)).length;
  // Unread message = jis conversation ka aakhri message customer ne bheja
  // ho (seller ne nahi) — pehle sirf "pichle 1 ghante mein activity" check
  // hoti thi jo asal read/unread status nahi tha
  const unreadMessages = conversations.filter((c) => {
    const lastChat = c.kmchats?.[0];
    return lastChat && lastChat.send_by !== 'seller';
  }).length;

  return (
    <div className="sel-topbar">

      {/* Toggle Button */}
      <button className="sel-topbar__toggle" onClick={() => toggleSidebar()}>
        {isMobile ? (
          isMobileOpen ? <MdClose /> : <MdMenu />
        ) : (
          isCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />
        )}
      </button>

      {/* Right Actions */}
      <div className="sel-topbar__actions">

        {/* Messages */}
        <div className="sel-topbar__dropdown-wrap" ref={messageRef}>
          <button
            className="sel-topbar__icon-btn"
            onClick={() => { setShowMessages(!showMessages); setShowNotifications(false); }}
          >
            <MdEmail />
            {unreadMessages > 0 && (
              <span className="sel-topbar__badge">{unreadMessages}</span>
            )}
          </button>

          {showMessages && (
            <div className="sel-topbar__dropdown">
              <div className="sel-topbar__dropdown-header">
                <h6>Messages</h6>
                <button className="sel-topbar__close-btn" onClick={() => setShowMessages(false)}>
                  <MdClose />
                </button>
              </div>
              <div className="sel-topbar__dropdown-body">
                {conversations.map((conv) => {
                  const lastChat = conv.kmchats?.[0];
                  const isUnreadMsg = lastChat && lastChat.send_by !== 'seller';
                  return (
                    <div
                      key={conv.id}
                      className={`sel-topbar__dropdown-item ${isUnreadMsg ? 'sel-topbar__dropdown-item--unread' : ''}`}
                      onClick={() => navigate(`/seller/messages?conversation=${conv.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="sel-topbar__msg-avatar">{conv.kmcustomer?.name?.charAt(0)}</div>
                      <div className="sel-topbar__msg-content">
                        <h6>{conv.kmcustomer?.name}</h6>
                        <p>{conv.lastMessage}</p>
                        <span>{getTimeAgo(conv.updated_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="sel-topbar__dropdown-footer">
                <Link to="/seller/messages">View All Messages</Link>
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="sel-topbar__dropdown-wrap" ref={notificationRef}>
          <button
            className="sel-topbar__icon-btn"
            onClick={() => { setShowNotifications(!showNotifications); setShowMessages(false); }}
          >
            <MdNotifications />
            {unreadNotifications > 0 && (
              <span className="sel-topbar__badge">{unreadNotifications}</span>
            )}
          </button>

          {showNotifications && (
            <div className="sel-topbar__dropdown">
              <div className="sel-topbar__dropdown-header">
                <h6>Notifications</h6>
                <button className="sel-topbar__close-btn" onClick={() => setShowNotifications(false)}>
                  <MdClose />
                </button>
              </div>
              <div className="sel-topbar__dropdown-body">
                {notifications.filter((n) => isUnread(n.status)).map((notif) => (
                  <div
                    key={notif.id}
                    className="sel-topbar__dropdown-item sel-topbar__dropdown-item--unread"
                    onClick={() => openNotification(notif)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="sel-topbar__notif-dot"></div>
                    <div className="sel-topbar__notif-content">
                      <h6>{notif.title}</h6>
                      <p>{notif.message}</p>
                      <span>{getTimeAgo(notif.created_at)}</span>
                    </div>
                  </div>
                ))}
                {notifications.filter((n) => isUnread(n.status)).length === 0 && (
                  <div className="sel-topbar__dropdown-empty">No new notifications</div>
                )}
              </div>
              <div className="sel-topbar__dropdown-footer">
                <Link to="/seller/notifications">View All Notifications</Link>
              </div>
            </div>
          )}
        </div>

        {/* Profile - name & role only, no avatar */}
        <Dropdown>
          <Dropdown.Toggle as="div" className="sel-topbar__profile">
            <div className="sel-topbar__profile-info">
              {/* name hidden on mobile via React */}
              {!isMobile && <h6>{displaySeller?.name}</h6>}
              <p>SELLER</p>
            </div>
          </Dropdown.Toggle>

          <Dropdown.Menu align="end">
            <Dropdown.Item as={Link} to="/seller/settings">
              <MdPerson /> My Profile
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/seller/settings">
              <MdSettings /> Settings
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => switchToUser()} className="sel-topbar__switch-item">
              <MdSwapHoriz /> Switch to User
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

      </div>
    </div>
  );
};

export default SellerTopbar;
